import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, Lock, Unlock, KeyRound, Fingerprint, Loader2, AlertTriangle,
  Save, Trash2, Plus, Check, Database, BadgeCheck,
} from 'lucide-react';
import { useZkVault } from '../services/zkVault/hooks';
import { AttributeDefinition, AttributeValue, ATTRIBUTE_OPTIONS } from '../services/zkVault/types';

const PIN_MIN_LENGTH = 6;

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`bg-slate-800 rounded-2xl border border-slate-700 shadow-lg p-6 ${className || ''}`}>{children}</div>
);
const inputCls =
  'w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors text-sm';
const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-start gap-3 bg-red-900/20 border border-red-500/40 text-red-200 px-4 py-3 rounded-xl text-sm">
    <AlertTriangle size={16} className="shrink-0 text-red-400 mt-0.5" /><span>{message}</span>
  </div>
);

const Header: React.FC = () => (
  <div className="flex items-start gap-3">
    <div className="h-12 w-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
      <ShieldCheck className="text-indigo-400" size={26} />
    </div>
    <div>
      <h1 className="text-xl font-bold text-white tracking-tight">Privacy Vault</h1>
      <p className="text-slate-400 text-sm mt-0.5">
        Your private attributes are encrypted on this device. Tinaney's servers only store
        unreadable ciphertext — and you're rewarded when you choose to share.
      </p>
    </div>
  </div>
);

const PrivacyVaultView: React.FC = () => {
  const vault = useZkVault();

  if (vault.status === 'loading') {
    return (
      <div className="py-24 flex flex-col items-center justify-center gap-4 text-slate-500">
        <Loader2 className="animate-spin text-indigo-500" size={40} />
        <p className="animate-pulse text-sm">Opening your secure vault…</p>
      </div>
    );
  }

  if (vault.status === 'error') {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        <Header />
        <Card>
          <ErrorBanner message={vault.error || 'Something went wrong.'} />
          <button onClick={() => vault.refresh()} className="mt-4 text-sm text-indigo-400 hover:text-indigo-300">Retry</button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Header />
      {vault.status === 'uninitialized' && <div className="max-w-md"><SetupPanel /></div>}
      {vault.status === 'locked' && <div className="max-w-md"><UnlockPanel /></div>}
      {vault.status === 'unlocked' && <UnlockedPanel />}
    </div>
  );
};

const SetupPanel: React.FC = () => {
  const vault = useZkVault();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const submit = async () => {
    setLocalErr(null);
    if (pin.length < PIN_MIN_LENGTH) return setLocalErr(`PIN must be at least ${PIN_MIN_LENGTH} characters.`);
    if (pin !== confirm) return setLocalErr('PINs do not match.');
    if (!ack) return setLocalErr('Please acknowledge the recovery warning.');
    setBusy(true);
    try { await vault.setupWithPin(pin); } catch { /* surfaced */ }
    finally { setBusy(false); setPin(''); setConfirm(''); }
  };

  return (
    <Card>
      <h2 className="text-white font-bold flex items-center gap-2 mb-1"><KeyRound size={18} className="text-indigo-400" /> Create your vault</h2>
      <p className="text-slate-400 text-sm mb-5">Choose a PIN (a memorable passphrase is best). It encrypts your data and never leaves your device. Add a passkey afterwards for one-tap unlocking.</p>
      <div className="space-y-4">
        <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} className={inputCls} placeholder={`PIN — at least ${PIN_MIN_LENGTH} characters`} autoComplete="new-password" />
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="Confirm PIN" autoComplete="new-password" />
        <label className="flex items-start gap-3 text-sm text-slate-300 bg-yellow-900/10 border border-yellow-900/40 rounded-xl p-3">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 accent-indigo-500" />
          <span>I understand that if I forget my PIN and have no passkey, my data is <strong className="text-yellow-300">permanently unrecoverable</strong>.</span>
        </label>
        {(localErr || vault.error) && <ErrorBanner message={localErr || vault.error!} />}
        <button onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-lg transition-colors">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}{busy ? 'Creating…' : 'Create encrypted vault'}
        </button>
      </div>
    </Card>
  );
};

const UnlockPanel: React.FC = () => {
  const vault = useZkVault();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState<'pin' | 'passkey' | null>(null);
  const canPasskey = vault.hasPasskey && vault.capability.webauthnSupported;

  const unlockPin = async () => { setBusy('pin'); try { await vault.unlockWithPin(pin); } catch { /* */ } finally { setBusy(null); setPin(''); } };
  const unlockPasskey = async () => { setBusy('passkey'); try { await vault.unlockWithPasskey(); } catch { /* */ } finally { setBusy(null); } };

  return (
    <Card>
      <h2 className="text-white font-bold flex items-center gap-2 mb-1"><Lock size={18} className="text-indigo-400" /> Vault locked</h2>
      <p className="text-slate-400 text-sm mb-5">Unlock to view or edit your private data.</p>
      <div className="space-y-4">
        {vault.hasPin && (
          <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && pin && unlockPin()} className={inputCls} placeholder="PIN / passphrase" autoComplete="current-password" />
        )}
        {vault.error && <ErrorBanner message={vault.error} />}
        {vault.hasPin && (
          <button onClick={unlockPin} disabled={!pin || busy !== null} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-lg transition-colors">
            {busy === 'pin' ? <Loader2 className="animate-spin" size={18} /> : <Unlock size={18} />} Unlock with PIN
          </button>
        )}
        {canPasskey && (
          <button onClick={unlockPasskey} disabled={busy !== null} className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 rounded-lg transition-colors">
            {busy === 'passkey' ? <Loader2 className="animate-spin" size={18} /> : <Fingerprint size={18} />} Unlock with passkey
          </button>
        )}
      </div>
    </Card>
  );
};

const valueToInput = (v: AttributeValue | undefined, type: string): any => {
  if (v === undefined || v === null) return type === 'BOOLEAN' ? false : '';
  return v;
};

const UnlockedPanel: React.FC = () => {
  const vault = useZkVault();
  const [draft, setDraft] = useState<Record<string, AttributeValue>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft({ ...vault.attributes }); }, [vault.attributes]);

  const grouped = useMemo(() => {
    const g: Record<string, AttributeDefinition[]> = {};
    for (const d of vault.definitions) { (g[d.category] ||= []).push(d); }
    return g;
  }, [vault.definitions]);

  const set = (id: string, v: AttributeValue) => setDraft((d) => ({ ...d, [id]: v }));

  const saveAll = async () => {
    setSaving(true); setSaved(false);
    try {
      for (const def of vault.definitions) {
        const next = draft[def.id];
        const prev = vault.attributes[def.id];
        const nextEmpty = next === undefined || next === null || next === '';
        if (nextEmpty && prev !== undefined) { await vault.removeAttribute(def.id); continue; }
        if (!nextEmpty && next !== prev) { await vault.saveAttribute(def.id, next); }
      }
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch { /* surfaced */ } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
      <div className="xl:col-span-2">
      <Card>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-bold flex items-center gap-2"><Unlock size={18} className="text-emerald-400" /> Your private data</h2>
          <button onClick={() => vault.lock()} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-slate-900 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors">
            <Lock size={13} /> Lock now
          </button>
        </div>

        {vault.definitions.length === 0 && (
          <p className="text-slate-500 text-sm italic">No attribute catalog found. Run supabase/datafi.sql to seed definitions.</p>
        )}

        <div className="space-y-6">
          {Object.entries(grouped).map(([category, defs]) => (
            <div key={category}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">{category}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {defs.map((def) => (
                  <label key={def.id} className="block">
                    <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                      {def.label_en}
                      {vault.attributes[def.id] !== undefined && (
                        <BadgeCheck size={12} className="text-emerald-400" />
                      )}
                    </span>
                    <div className="mt-1.5">
                      {def.data_type === 'BOOLEAN' ? (
                        <button type="button" onClick={() => set(def.id, !draft[def.id])}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium w-full ${draft[def.id] ? 'bg-indigo-900/30 border-indigo-500/50 text-white' : 'bg-slate-900/40 border-slate-700 text-slate-400'}`}>
                          <span className={`w-4 h-4 rounded border flex items-center justify-center ${draft[def.id] ? 'bg-indigo-500 border-indigo-500' : 'border-slate-600'}`}>
                            {draft[def.id] && <Check size={11} className="text-white" />}
                          </span>
                          {draft[def.id] ? 'Yes' : 'No'}
                        </button>
                      ) : def.data_type === 'SELECTION' ? (
                        <select value={String(valueToInput(draft[def.id], def.data_type))} onChange={(e) => set(def.id, e.target.value)} className={inputCls}>
                          <option value="">—</option>
                          {(ATTRIBUTE_OPTIONS[def.id] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : def.data_type === 'NUMBER' ? (
                        <input type="number" value={String(valueToInput(draft[def.id], def.data_type))}
                          onChange={(e) => set(def.id, e.target.value === '' ? '' : Number(e.target.value))} className={inputCls} />
                      ) : (
                        <input type="text" value={String(valueToInput(draft[def.id], def.data_type))} onChange={(e) => set(def.id, e.target.value)} className={inputCls} placeholder={def.label_en} />
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {vault.error && <div className="mt-4"><ErrorBanner message={vault.error} /></div>}

        {vault.definitions.length > 0 && (
          <button onClick={saveAll} disabled={saving} className="mt-6 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-lg transition-colors">
            {saving ? <Loader2 className="animate-spin" size={18} /> : saved ? <Check size={18} /> : <Save size={18} />}
            {saving ? 'Encrypting & saving…' : saved ? 'Saved' : 'Save (encrypted)'}
          </button>
        )}
      </Card>
      </div>

      <SettingsPanel />
    </div>
  );
};

const SettingsPanel: React.FC = () => {
  const vault = useZkVault();
  const [busy, setBusy] = useState<string | null>(null);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPinForm, setShowPinForm] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const passkeyPossible = vault.capability.webauthnSupported;

  const addPasskey = async () => { setBusy('add'); setMsg(null); setLocalErr(null); try { await vault.addPasskey(); setMsg('Passkey added.'); } catch (e: any) { setLocalErr(e?.message || 'Could not add passkey.'); } finally { setBusy(null); } };
  const removePasskey = async () => { setBusy('remove'); setMsg(null); setLocalErr(null); try { await vault.removePasskey(); setMsg('Passkey removed.'); } catch (e: any) { setLocalErr(e?.message || 'Could not remove passkey.'); } finally { setBusy(null); } };
  const changePin = async () => {
    setLocalErr(null); setMsg(null);
    if (newPin.length < PIN_MIN_LENGTH) return setLocalErr(`PIN must be at least ${PIN_MIN_LENGTH} characters.`);
    if (newPin !== confirmPin) return setLocalErr('PINs do not match.');
    setBusy('pin');
    try { await vault.changePin(newPin); setMsg('PIN updated.'); setShowPinForm(false); setNewPin(''); setConfirmPin(''); }
    catch (e: any) { setLocalErr(e?.message || 'Could not change PIN.'); } finally { setBusy(null); }
  };

  return (
    <Card>
      <h2 className="text-white font-bold flex items-center gap-2 mb-4"><KeyRound size={18} className="text-indigo-400" /> Unlock methods</h2>
      {(msg || localErr) && (
        <div className="mb-4">
          {localErr ? <ErrorBanner message={localErr} /> : (
            <div className="flex items-center gap-2 bg-emerald-900/20 border border-emerald-500/40 text-emerald-200 px-4 py-2.5 rounded-xl text-sm"><Check size={15} /> {msg}</div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between py-3 border-b border-slate-700/60">
        <div className="flex items-center gap-3">
          <Fingerprint size={18} className="text-slate-400" />
          <div>
            <p className="text-slate-200 text-sm font-medium">Passkey</p>
            <p className="text-slate-500 text-xs">{vault.hasPasskey ? 'Enabled — unlock with biometrics/device.' : passkeyPossible ? 'Add a passkey for one-tap unlocking.' : 'Not supported on this browser/device.'}</p>
          </div>
        </div>
        {vault.hasPasskey ? (
          <button onClick={removePasskey} disabled={busy !== null} className="flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200 bg-red-900/20 border border-red-900/50 px-3 py-1.5 rounded-lg">
            {busy === 'remove' ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />} Remove
          </button>
        ) : (
          <button onClick={addPasskey} disabled={!passkeyPossible || busy !== null} className="flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200 bg-indigo-900/20 border border-indigo-900/50 px-3 py-1.5 rounded-lg disabled:opacity-40">
            {busy === 'add' ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Add
          </button>
        )}
      </div>
      <div className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <KeyRound size={18} className="text-slate-400" />
            <div><p className="text-slate-200 text-sm font-medium">PIN / passphrase</p><p className="text-slate-500 text-xs">Always available as a portable fallback.</p></div>
          </div>
          <button onClick={() => setShowPinForm((s) => !s)} className="text-xs text-indigo-300 hover:text-indigo-200 bg-indigo-900/20 border border-indigo-900/50 px-3 py-1.5 rounded-lg">Change</button>
        </div>
        {showPinForm && (
          <div className="mt-4 space-y-3">
            <input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value)} className={inputCls} placeholder="New PIN" autoComplete="new-password" />
            <input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} className={inputCls} placeholder="Confirm new PIN" autoComplete="new-password" />
            <button onClick={changePin} disabled={busy !== null} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-semibold py-2.5 rounded-lg text-sm">
              {busy === 'pin' ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Update PIN
            </button>
          </div>
        )}
      </div>
      <p className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5">
        <Database size={12} /> Stored as ciphertext only — the server can't read your data, and it syncs to any device where you sign in.
      </p>
    </Card>
  );
};

export default PrivacyVaultView;
